import React, { useEffect, useRef, useState } from "react";
import {
  Plus,
  Pencil,
  Trash2,
  GripVertical,
  X,
  UploadCloud,
  Save,
  AlertCircle,
  Crown,
  User as UserIcon,
} from "lucide-react";
import {
  adminListTeam,
  adminCreateTeam,
  adminUpdateTeam,
  adminDeleteTeam,
  adminReorderTeam,
  adminUpload,
  resolveAssetUrl,
} from "../../lib/api";

const ICON_OPTIONS = ["Award", "Star", "Camera", "Trophy", "Sparkles", "Heart", "Zap"];

const empty = () => ({
  name: "",
  role: "",
  role_label: "Creator",
  bio: "",
  portrait: "",
  is_founder: false,
  badges: [],
  instagram: "",
  tiktok: "",
});

const PortraitUpload = ({ value, onChange }) => {
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);

  const upload = async (file) => {
    setBusy(true);
    setProgress(0);
    try {
      const res = await adminUpload(file, setProgress);
      onChange(res.url);
    } catch (e) {
      alert(e?.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  if (value) {
    return (
      <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10">
        <div className="w-20 h-24 rounded-lg overflow-hidden bg-neutral-800">
          <img src={resolveAssetUrl(value)} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs text-neutral-400 truncate">{value}</div>
        </div>
        <button
          type="button"
          onClick={() => onChange("")}
          className="p-2 rounded-full text-neutral-400 hover:text-red-400 hover:bg-red-500/10"
        >
          <X size={14} />
        </button>
      </div>
    );
  }

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDrag(true);
      }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDrag(false);
        const f = e.dataTransfer.files?.[0];
        if (f) upload(f);
      }}
      onClick={() => inputRef.current?.click()}
      className={`cursor-pointer rounded-2xl border-2 border-dashed p-6 text-center transition-colors ${
        drag ? "border-pink-400 bg-pink-500/10" : "border-white/15 bg-white/5 hover:border-pink-400/50"
      }`}
      data-testid="team-portrait-dropzone"
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) upload(f);
        }}
      />
      <UploadCloud size={24} className="mx-auto text-pink-400 mb-2" />
      <div className="text-sm font-bold text-white">
        {busy ? `Hochladen… ${progress}%` : "Portrait hochladen"}
      </div>
      <div className="text-xs text-neutral-400 mt-1">JPG / PNG / WEBP – Hochformat empfohlen</div>
    </div>
  );
};

const MemberForm = ({ initial, onClose, onSaved }) => {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setBadge = (idx, key, value) => {
    setForm((f) => {
      const badges = [...f.badges];
      badges[idx] = { ...badges[idx], [key]: value };
      return { ...f, badges };
    });
  };
  const addBadge = () =>
    setForm((f) => ({ ...f, badges: [...f.badges, { icon: "Star", text: "" }] }));
  const removeBadge = (idx) =>
    setForm((f) => ({ ...f, badges: f.badges.filter((_, i) => i !== idx) }));

  const save = async () => {
    if (!form.name.trim()) {
      setErr("Name ist erforderlich");
      return;
    }
    setSaving(true);
    setErr("");
    try {
      const saved = initial.id
        ? await adminUpdateTeam(initial.id, form)
        : await adminCreateTeam(form);
      onSaved(saved);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end lg:items-center justify-center p-0 lg:p-6"
      onClick={onClose}
    >
      <div
        className="w-full lg:max-w-2xl bg-neutral-950 border border-white/10 rounded-t-3xl lg:rounded-3xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-neutral-950 px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-pink-400">
              {initial.id ? "Bearbeiten" : "Neues Team-Mitglied"}
            </div>
            <div className="font-display font-extrabold text-xl text-white truncate">
              {form.name || "Mitglied"}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/10 text-neutral-400"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 block mb-2">
              Portrait
            </label>
            <PortraitUpload value={form.portrait} onChange={(v) => setField("portrait", v)} />
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 block mb-1">
                Name *
              </label>
              <input
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
                placeholder="z. B. Keanu Irle"
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-pink-400 outline-none text-white placeholder:text-neutral-500"
                data-testid="team-name"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 block mb-1">
                Rolle (Label)
              </label>
              <select
                value={form.role_label}
                onChange={(e) => setField("role_label", e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-pink-400 outline-none text-white"
              >
                {["Founder", "Co-Founder", "Strategist", "Creator", "Editor", "Account Manager", "Team"].map((r) => (
                  <option key={r} value={r} className="bg-neutral-900">
                    {r}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 block mb-1">
              Position / Titel
            </label>
            <input
              value={form.role}
              onChange={(e) => setField("role", e.target.value)}
              placeholder="z. B. Social Media Strategist"
              className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-pink-400 outline-none text-white placeholder:text-neutral-500"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 block mb-1">
              Bio / Beschreibung
            </label>
            <textarea
              value={form.bio}
              onChange={(e) => setField("bio", e.target.value)}
              rows={6}
              placeholder="Erzähl etwas über dieses Team-Mitglied. Leerzeile = neuer Absatz."
              className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-pink-400 outline-none text-white placeholder:text-neutral-500 resize-y"
            />
          </div>

          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_founder}
                onChange={(e) => setField("is_founder", e.target.checked)}
                className="w-4 h-4 accent-pink-500"
              />
              <div>
                <div className="text-sm font-bold text-white">Founder-Karte (groß, oben)</div>
                <div className="text-[11px] text-neutral-400">
                  Founder werden mit großem Hero-Layout angezeigt. Andere als Grid-Karten darunter.
                </div>
              </div>
            </label>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">
                Badges (Auszeichnungen / Skills)
              </label>
              <button
                type="button"
                onClick={addBadge}
                className="text-xs font-bold text-pink-400 hover:text-pink-300 inline-flex items-center gap-1"
              >
                <Plus size={12} /> Badge
              </button>
            </div>
            <div className="space-y-2">
              {form.badges.map((b, idx) => (
                <div key={idx} className="flex gap-2">
                  <select
                    value={b.icon}
                    onChange={(e) => setBadge(idx, "icon", e.target.value)}
                    className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 outline-none text-white text-xs"
                  >
                    {ICON_OPTIONS.map((i) => (
                      <option key={i} value={i} className="bg-neutral-900">
                        {i}
                      </option>
                    ))}
                  </select>
                  <input
                    value={b.text}
                    onChange={(e) => setBadge(idx, "text", e.target.value)}
                    placeholder="z. B. Top UGC Creator 2024"
                    className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 outline-none text-white text-sm placeholder:text-neutral-500"
                  />
                  <button
                    type="button"
                    onClick={() => removeBadge(idx)}
                    className="p-2 rounded-lg text-neutral-400 hover:text-red-400 hover:bg-red-500/10"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              {form.badges.length === 0 && (
                <div className="text-xs text-neutral-500 italic">Noch keine Badges.</div>
              )}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 block mb-1">
                Instagram (optional)
              </label>
              <input
                value={form.instagram}
                onChange={(e) => setField("instagram", e.target.value)}
                placeholder="https://instagram.com/…"
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-pink-400 outline-none text-white placeholder:text-neutral-500 text-sm"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 block mb-1">
                TikTok (optional)
              </label>
              <input
                value={form.tiktok}
                onChange={(e) => setField("tiktok", e.target.value)}
                placeholder="https://tiktok.com/@…"
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-pink-400 outline-none text-white placeholder:text-neutral-500 text-sm"
              />
            </div>
          </div>

          {err && (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm flex items-center gap-2">
              <AlertCircle size={14} />
              {err}
            </div>
          )}

          <div className="pt-2">
            <button
              onClick={save}
              disabled={saving}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-pink-500 via-orange-500 to-purple-500 text-white text-sm font-bold uppercase tracking-wider hover:-translate-y-[2px] transition-all disabled:opacity-60"
              data-testid="team-save-btn"
            >
              <Save size={14} />
              {saving ? "Speichern…" : "Speichern"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const TeamAdminPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const dragId = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminListTeam();
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const onSaved = (saved) => {
    setItems((prev) => {
      const exists = prev.find((p) => p.id === saved.id);
      if (exists) return prev.map((p) => (p.id === saved.id ? saved : p));
      return [...prev, saved];
    });
    setEditing(null);
  };

  const remove = async (m) => {
    if (!window.confirm(`„${m.name}" wirklich aus dem Team entfernen?`)) return;
    await adminDeleteTeam(m.id);
    setItems((prev) => prev.filter((p) => p.id !== m.id));
  };

  const onDragStart = (id) => (dragId.current = id);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = async (targetId) => {
    const sourceId = dragId.current;
    dragId.current = null;
    if (!sourceId || sourceId === targetId) return;
    const reordered = [...items];
    const fromIdx = reordered.findIndex((i) => i.id === sourceId);
    const toIdx = reordered.findIndex((i) => i.id === targetId);
    if (fromIdx < 0 || toIdx < 0) return;
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    setItems(reordered);
    try {
      await adminReorderTeam(reordered.map((r) => r.id));
    } catch {
      load();
    }
  };

  return (
    <div data-testid="admin-team-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] font-bold text-pink-400">Team</div>
          <h1 className="mt-1 font-display font-extrabold text-3xl md:text-5xl tracking-tight">
            Team verwalten
          </h1>
          <p className="mt-2 text-sm text-neutral-400 max-w-xl">
            Founder, Creators, Editors – alle Mitglieder mit Foto, Bio, Badges & Social-Links. Drag & Drop sortiert die Reihenfolge auf der Team-Seite.
          </p>
        </div>
        <button
          onClick={() => setEditing(empty())}
          className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-gradient-to-r from-pink-500 via-orange-500 to-purple-500 text-white text-sm font-bold uppercase tracking-wider hover:-translate-y-[2px] transition-all"
          data-testid="team-add-btn"
        >
          <Plus size={14} /> Neues Mitglied
        </button>
      </div>

      <div className="rounded-2xl bg-white/5 border border-white/10 divide-y divide-white/5">
        {loading && (
          <div className="p-8 text-center text-sm text-neutral-400">Laden…</div>
        )}
        {!loading && items.length === 0 && (
          <div className="p-10 text-center text-sm text-neutral-400">
            Noch keine Team-Mitglieder.
          </div>
        )}
        {!loading &&
          items.map((m) => (
            <div
              key={m.id}
              draggable
              onDragStart={() => onDragStart(m.id)}
              onDragOver={onDragOver}
              onDrop={() => onDrop(m.id)}
              className="flex items-center gap-3 p-3 md:p-4 hover:bg-white/5 transition-colors group"
              data-testid={`team-row-${m.id}`}
            >
              <div className="text-neutral-600 group-hover:text-neutral-300 cursor-grab active:cursor-grabbing">
                <GripVertical size={18} />
              </div>
              <div className="w-14 h-16 rounded-lg overflow-hidden bg-neutral-800 shrink-0">
                {m.portrait ? (
                  <img src={resolveAssetUrl(m.portrait)} alt={m.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <UserIcon size={20} className="text-neutral-500" />
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-bold text-white truncate">{m.name}</div>
                  {m.is_founder && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-pink-500/15 text-pink-300 border border-pink-500/30">
                      <Crown size={10} /> Founder
                    </span>
                  )}
                  <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-white/5 text-neutral-300 border border-white/10">
                    {m.role_label}
                  </span>
                </div>
                <div className="text-xs text-neutral-400 truncate mt-0.5">
                  {m.role}
                  {m.badges?.length ? ` · ${m.badges.length} Badge${m.badges.length === 1 ? "" : "s"}` : ""}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setEditing(m)}
                  className="p-2 rounded-full text-neutral-400 hover:text-white hover:bg-white/10"
                  data-testid={`team-edit-${m.id}`}
                >
                  <Pencil size={14} />
                </button>
                <button
                  onClick={() => remove(m)}
                  className="p-2 rounded-full text-neutral-400 hover:text-red-400 hover:bg-red-500/10"
                  data-testid={`team-delete-${m.id}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
      </div>

      {editing && (
        <MemberForm initial={editing} onClose={() => setEditing(null)} onSaved={onSaved} />
      )}
    </div>
  );
};

export default TeamAdminPage;
